import { daysBetween, type DayKey } from "./time.js";
import type { Area, GameState, ModeState, Size } from "./types.js";

/**
 * Form: how fit an area is right now, 0–1. Levels and XP (experience) never drop; form does, slowly,
 * after a grace period without activity, and comes back faster than it was built (muscle memory,
 * a short refresher for knowledge). Decision of the owner 2026-10-19.
 */
export interface FormRule {
  /** Days without activity before anything drops. */
  graceDays: number;
  /** Share of the distance to the floor lost per day after the grace period. */
  decay: number;
  /** Form never goes below this: what you built does not vanish. */
  floor: number;
  /** Share of the missing form one activity brings back (S, M, L). */
  refresh: Record<Size, number>;
}

export const FORM_RULES: Partial<Record<Area, FormRule>> = {
  // Fitness fades within weeks, about 60% after a month and a half off, and comes back in a week or two.
  body: { graceDays: 7, decay: 0.03, floor: 0.35, refresh: { S: 0.1, M: 0.25, L: 0.4 } },
  // Knowledge stays: slow fading, high floor, a short refresher brings a lot back.
  mind: { graceDays: 14, decay: 0.01, floor: 0.6, refresh: { S: 0.3, M: 0.5, L: 0.7 } },
  // Contacts loosen, a meeting warms them up again.
  social: { graceDays: 10, decay: 0.02, floor: 0.4, refresh: { S: 0.2, M: 0.35, L: 0.5 } },
  // Work and Home have no form (Home has the mess instead).
};

export type FormArea = "body" | "mind" | "social";
export const FORM_AREAS: readonly FormArea[] = ["body", "mind", "social"];

export interface FormPoint {
  /** Form on `day`, after that day's activity. */
  value: number;
  /** Day of the last activity in the area. */
  day: DayKey;
  /** Days since `day` spent in earlier sick leave or vacation (no decay then). */
  paused: number;
}

/** Days of `mode` (sick, vacation) inside (from, to]: form does not drop then. */
function pausedDays(from: DayKey, to: DayKey, mode: ModeState): number {
  if (mode.kind !== "sick" && mode.kind !== "vacation") return 0;
  const start = mode.since > from ? mode.since : from;
  const end = mode.until && mode.until < to ? mode.until : to;
  return start < end ? daysBetween(start, end) : 0;
}

/** Form of a point on `today`, with the decay since its day (pure). */
export function formOn(rule: FormRule, p: FormPoint, today: DayKey, mode: ModeState): number {
  const idle = daysBetween(p.day, today) - p.paused - pausedDays(p.day, today, mode);
  const days = Math.max(0, idle - rule.graceDays);
  if (!days) return p.value;
  const floor = Math.min(rule.floor, p.value);
  return floor + (p.value - floor) * Math.pow(1 - rule.decay, days);
}

/** Current form of every area that has one. */
export function formNow(state: GameState, today: DayKey): Record<FormArea, number> {
  const out = {} as Record<FormArea, number>;
  for (const a of FORM_AREAS) out[a] = formOn(FORM_RULES[a]!, state.stats.form[a], today, state.mode);
  return out;
}

/** Activity in `area` today: settle the decay, then bring back part of the missing form. */
export function refreshForm(state: GameState, area: Area, size: Size, today: DayKey): void {
  const rule = FORM_RULES[area];
  if (!rule) return;
  const v = formOn(rule, state.stats.form[area as FormArea], today, state.mode);
  state.stats.form[area as FormArea] = {
    value: Math.min(1, v + (1 - v) * rule.refresh[size]),
    day: today,
    paused: 0,
  };
}

/** Before the mode changes: remember the paused days of the ending sick leave or vacation. */
export function settleFormPause(state: GameState, today: DayKey): void {
  for (const a of FORM_AREAS) {
    const p = state.stats.form[a];
    p.paused += pausedDays(p.day, today, state.mode);
  }
}

/** Body stage shown on screen: one stage lower while body form is below half. */
export function visibleBodyStage(stage: number, bodyForm: number): number {
  return bodyForm < 0.5 ? Math.max(0, stage - 1) : stage;
}
